import type {
	OrderCheckFiringLevel,
	OrderCheckFlag,
	OrderCheckLevel,
} from './order-check-level';
import {
	isFlagOn,
	isOrderCheckOn,
	isOrderedQuantity,
} from './order-check-level';

/**
 * PD-16276 — stock on hand and short-dated items.
 *
 * Stock is the caller's to gather, and both callers scope it the same way:
 * summed over the warehouses linked to the client (client_warehouse). See
 * docs/claude/domain-notes.md, "Stock scoping".
 */

export interface StockLineModel {
	itemId: number;
	quantity: number;
	/** Client-scoped quantity on hand. Null or undefined means "not known", which is not checked. */
	quantityOnHand: number | null | undefined;
	/**
	 * A fee, deposit, rental or service charge is never physical stock, so it
	 * is never out of stock (PD-15021).
	 */
	isNonInventory?: OrderCheckFlag;
}

export interface OutOfStockFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	quantity: number;
	quantityOnHand: number;
}

export function checkOutOfStock(
	lines: ReadonlyArray<StockLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<OutOfStockFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	const findings: Array<OutOfStockFindingModel> = [];

	for (const line of lines) {
		const quantityOnHand = line.quantityOnHand;

		if (
			!isOrderedQuantity(line.quantity) ||
			isFlagOn(line.isNonInventory) ||
			quantityOnHand == null
		) {
			continue;
		}

		if (line.quantity > quantityOnHand || quantityOnHand <= 0) {
			findings.push({
				itemId: line.itemId,
				level,
				quantity: line.quantity,
				quantityOnHand,
			});
		}
	}

	return findings;
}

export interface ShortDateLineModel {
	itemId: number;
	quantity: number;
	/**
	 * The explicit ERP short-date flag on the item. Short-dated is that flag
	 * only, never expiration-date arithmetic (PD-10887).
	 */
	isShortDate: OrderCheckFlag;
}

export interface ShortDateFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
}

export function checkShortDate(
	lines: ReadonlyArray<ShortDateLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<ShortDateFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	return lines
		.filter(
			(line) =>
				isOrderedQuantity(line.quantity) &&
				// Raw database rows carry tinyint(1) as 1/0, entities as booleans.
				isFlagOn(line.isShortDate),
		)
		.map((line) => ({ itemId: line.itemId, level }));
}
